// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Archive,
  Plus,
  BriefcaseBusiness,
  SearchX,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useGetDeals } from "@/hooks/useDeals";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { getDealsExportService } from "@/services/deals.service";
import { BOARD_COLUMNS } from "@/utils/deals.utils";
import { Button } from "@/components/ui/button";
import DealBoard from "@/components/deals/DealBoard";
import DealControls from "@/components/deals/DealControls";
import DealTable from "@/components/deals/DealTable";
import NewDealLauncher from "@/components/deals/new-deal/NewDealLauncher";
import EmptyState from "@/components/common/EmptyState";
import PageHeader from "@/components/layout/PageHeader";
import PlanUsageBanner from "@/components/organization/PlanUsageBanner";
import ExportButton from "@/components/common/ExportButton";
import { downloadCsv } from "@/utils/common.utils";
import { dealExportRows } from "@/utils/deals.utils";
import DealBoardSkeleton from "@/components/deals/skeletons/DealBoardSkeleton";
import { EMPTY_ARRAY } from "@/utils/common.utils";
import { DealType, InternalRole } from "@/enum/common.enum";
import { PROTECTED_ROUTES } from "@/routes/url";

export default function Dashboard() {
  const { context } = useAuth();
  const [showWizard, setShowWizard] = useState(false);
  const [search, setSearch] = useState("");
  const [sideFilter, setSideFilter] = useState("all");
  const [view, setView] = useState("board");

  // Search and the side filter are server-side query params now. Debounced so a
  // burst of keystrokes is one request, and so the list can't settle on the
  // response to a query the user has already moved past.
  const debouncedSearch = useDebouncedValue(search, 300);
  const dealParams = useMemo(() => ({
    limit: 500,
    ...(debouncedSearch.trim() ? { q: debouncedSearch.trim() } : {}),
    ...(sideFilter !== "all" ? { deal_type: sideFilter as DealType } : {}),
  }), [debouncedSearch, sideFilter]);

  const { data: dealsData, isLoading: loading, refetch } = useGetDeals(dealParams);
  const deals = dealsData?.deals ?? EMPTY_ARRAY;

  const isInternal = Boolean(context?.membership);
  const canCreateDeal = isInternal;

  const load = () => { refetch(); };

  useEffect(() => {
    if (!loading && isInternal && deals.length === 0 && context?.membership?.role === InternalRole.ORG_OWNER) {
      const dismissed = sessionStorage.getItem("stoop_wizard_dismissed");
      if (!dismissed) setShowWizard(true);
    }
  }, [loading, deals.length, isInternal, context]);

  // The server already applied the filters, so what came back *is* the list.
  const filteredDeals = deals;

  // Each row carries its own `board_column`, computed server-side from its
  // phase. Grouping on it means the phase -> column mapping lives in one place,
  // and it is what a drag optimistically rewrites to move a card.
  const grouped = useMemo(() => BOARD_COLUMNS.reduce(
    (acc, column) => ({
      ...acc,
      [column.id]: filteredDeals.filter((deal) => deal.board_column === column.id),
    }),
    {}
  ), [filteredDeals]);

  const closeWizard = (nextOpen) => {
    if (nextOpen) { setShowWizard(true); return; }
    sessionStorage.setItem("stoop_wizard_dismissed", "1");
    setShowWizard(false);
    load();
  };

  // Exports the full matching set from the server, not the rows in memory —
  // same filters, no page cap.
  const exportDeals = async () => {
    const { rows } = await getDealsExportService(dealParams);
    if (!rows.length) {
      toast.info("There are no deals to export");
      return;
    }
    downloadCsv({ rows: dealExportRows(rows), filename: "deals.csv" });
  };

  return (
    <div className="mx-auto flex w-full max-w-[1680px] flex-col gap-8 font-dashboard" data-testid="deals-page">
      {context?.membership && <PlanUsageBanner usage={context?.billing?.usage} organizationSlug={context?.organization?.slug} addonsAvailable={(context?.billing?.usage?.deal_addon_available ?? context?.billing?.usage?.addons_available) !== false} />}

      <PageHeader
        title="Deals"
        description="Monitor and manage every transaction in your brokerage."
        actionsClassName="gap-3"
        actions={
          <>
          <Button variant="dashboardOutline" className="h-11 !rounded-xl" asChild>
            <Link to={PROTECTED_ROUTES.ARCHIVED_DEALS} data-testid="dashboard-view-archived"><Archive data-icon="inline-start" /> Archived</Link>
          </Button>
          <ExportButton
            onExport={exportDeals}
            className="h-11 !rounded-xl"
            testId="dashboard-export"
          />
          {canCreateDeal && (
            <NewDealLauncher className="h-11 !rounded-xl" />
          )}
          </>
        }
      />

      <DealControls search={search} onSearchChange={setSearch} sideFilter={sideFilter} onSideFilterChange={setSideFilter} view={view} onViewChange={setView} />

      {loading ? (
        <DealBoardSkeleton />
      ) : deals.length === 0 ? (
        <EmptyDeals canCreateDeal={canCreateDeal} onCreate={() => setShowWizard(true)} />
      ) : filteredDeals.length === 0 ? (
        <EmptyState icon={SearchX} title="No matching deals" description="No deals match the current search and filters. Clear them to see your full pipeline." action={<Button type="button" variant="outline" className="mt-5" onClick={() => { setSearch(""); setSideFilter("all"); }}>Clear filters</Button>} />
      ) : view === "board" ? (
        <DealBoard grouped={grouped} canDrag={isInternal} />
      ) : (
        <DealTable deals={filteredDeals} />
      )}

      {canCreateDeal && <NewDealLauncher hideTrigger open={showWizard} onOpenChange={closeWizard} />}
    </div>
  );
}

function EmptyDeals({ canCreateDeal, onCreate }) {
  return (
    <EmptyState
      title="No deals yet"
      icon={BriefcaseBusiness}
      description={canCreateDeal ? "Open your first deal to start tracking the transaction from one calm workspace." : "Your agent will invite you when a deal is ready."}
      action={canCreateDeal ? <Button type="button" variant="dashboardPrimary" className="mt-6" onClick={onCreate} data-testid="empty-new-deal-btn"><Plus data-icon="inline-start" /> Open a deal</Button> : null}
    />
  );
}
